import {
    ref, computed, onMounted, onUnmounted,
} from 'vue';
import { BreakpointsEnum } from '@/js/enums/breakpoints';

export function useDeviceSize() {
    const isMobile = ref(false);
    const isTablet = ref(false);
    const isLaptop = ref(false);
    const isDesktop = ref(false);
    const isLargeDesktop = ref(false);

    const isMobileOrTablet = computed(() => isMobile.value || isTablet.value);

    const mobileMediaQuery = `(max-width: ${BreakpointsEnum.MOBILE_WIDTH_IN_PX - 1}px)`;
    const tabletMediaQuery = `(min-width: ${BreakpointsEnum.MOBILE_WIDTH_IN_PX}px) and (max-width: ${BreakpointsEnum.TABLET_WIDTH_IN_PX - 1}px)`;
    const laptopMediaQuery = `(min-width: ${BreakpointsEnum.TABLET_WIDTH_IN_PX}px) and (max-width: ${BreakpointsEnum.LAPTOP_WIDTH_IN_PX - 1}px)`;
    const desktopMediaQuery = `(min-width: ${BreakpointsEnum.LAPTOP_WIDTH_IN_PX}px) and (max-width: ${BreakpointsEnum.DESKTOP_WIDTH_IN_PX - 1}px)`;
    const largeDesktopMediaQuery = `(min-width: ${BreakpointsEnum.DESKTOP_WIDTH_IN_PX}px)`;

    let mobileMatcher: MediaQueryList | undefined;
    let tabletMatcher: MediaQueryList | undefined;
    let laptopMatcher: MediaQueryList | undefined;
    let desktopMatcher: MediaQueryList | undefined;
    let largeDesktopMatcher: MediaQueryList | undefined;

    const updateIsMobile = (event: MediaQueryListEvent | MediaQueryList) => { isMobile.value = event.matches; };

    const updateIsTablet = (event: MediaQueryListEvent | MediaQueryList) => { isTablet.value = event.matches; };

    const updateIsLaptop = (event: MediaQueryListEvent | MediaQueryList) => { isLaptop.value = event.matches; };

    const updateIsDesktop = (event: MediaQueryListEvent | MediaQueryList) => { isDesktop.value = event.matches; };

    const updateIsLargeDesktop = (event: MediaQueryListEvent | MediaQueryList) => { isLargeDesktop.value = event.matches; };

    const setInitialValues = () => {
        mobileMatcher = window.matchMedia(mobileMediaQuery);
        updateIsMobile(mobileMatcher);

        tabletMatcher = window.matchMedia(tabletMediaQuery);
        updateIsTablet(tabletMatcher);

        laptopMatcher = window.matchMedia(laptopMediaQuery);
        updateIsLaptop(laptopMatcher);

        desktopMatcher = window.matchMedia(desktopMediaQuery);
        updateIsDesktop(desktopMatcher);

        largeDesktopMatcher = window.matchMedia(largeDesktopMediaQuery);
        updateIsLargeDesktop(largeDesktopMatcher);
    };

    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
        setInitialValues();
    }

    onMounted(() => {
        if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
            if (!mobileMatcher) {
                setInitialValues();
            }

            if (typeof mobileMatcher?.addEventListener !== 'function') {
                return;
            }

            mobileMatcher?.addEventListener('change', updateIsMobile);
            tabletMatcher?.addEventListener('change', updateIsTablet);
            laptopMatcher?.addEventListener('change', updateIsLaptop);
            desktopMatcher?.addEventListener('change', updateIsDesktop);
            largeDesktopMatcher?.addEventListener('change', updateIsLargeDesktop);
        }
    });

    onUnmounted(() => {
        if (typeof mobileMatcher?.removeEventListener !== 'function') {
            return;
        }

        mobileMatcher?.removeEventListener('change', updateIsMobile);
        tabletMatcher?.removeEventListener('change', updateIsTablet);
        laptopMatcher?.removeEventListener('change', updateIsLaptop);
        desktopMatcher?.removeEventListener('change', updateIsDesktop);
        largeDesktopMatcher?.removeEventListener('change', updateIsLargeDesktop);
    });

    return {
        isMobile,
        isTablet,
        isLaptop,
        isDesktop,
        isLargeDesktop,
        isMobileOrTablet,
    };
}
